// A practice in progress, as data, so that leaving the board page does not throw it away.
//
// Every page is a page of its own: opening /settings to check something unloads the board, the
// Chessground instance and the session with it. What the user saw was their moves gone. This turns
// the session into something JSON survives, and back.
//
// `moves` is indexed by ply and `attempts` keeps every move ever played, take-backs included, so a
// user move belongs to both. The two arrays share the record by reference — combos and XP are
// recomputed over `moves` and read back through `attempts` — and a reference is exactly what
// JSON.stringify cannot keep. So a user move is stored once, in `attempts`, and `moves` points at
// it by index.

export const SNAPSHOT_VERSION = 2;

/** How long a practice waits on this tab before it is not worth coming back to. */
export const SNAPSHOT_MAX_AGE_MS = 6 * 60 * 60 * 1000;

/** The statuses a practice can be resumed in. Finished and locked ones have nowhere to continue. */
const RESUMABLE = ["ready", "playing", "offbook"];

export interface SnapshotSession {
    id: string;
    startedAt: string;
    startingFen: string | null;
    hooksShown: number;
    takebacks: number;
    hints: number;
    isRetry: boolean;
    maxOwnMoves: number;
    bestCombo: number;
}

export interface PracticeSnapshot {
    v: number;
    savedAt: number;
    status: string;
    line: Record<string, unknown>;
    /** SAN of every move on the board, the line's own moves included. */
    history: string[];
    session: SnapshotSession;
    attempts: Record<string, unknown>[];
    /** Indexed by ply: null where the line played, { attempt } for a user move, the record for a reply. */
    moves: (Record<string, unknown> | { attempt: number } | null)[];
    offbook: Record<string, unknown> | null;
}

interface LiveSession {
    id: string;
    line: Record<string, unknown>;
    startedAt: string;
    startingFen: string | null;
    moves: (Record<string, unknown> | undefined)[];
    attempts: Record<string, unknown>[];
    hooksShown?: number;
    takebacks?: number;
    hints?: number;
    isRetry?: boolean;
    maxOwnMoves?: number;
    bestCombo?: number;
}

/** Everything but the promise a move carries while its verdict is in flight. */
function plain(move: Record<string, unknown>): Record<string, unknown> {
    const { settled, ...rest } = move;
    return rest;
}

/**
 * The practice as it stands, or null when there is nothing worth coming back to: no session, one
 * that has ended, or the login wall.
 */
export function snapshotOf({ session, status, history, offbook, now = Date.now() }: {
    session: LiveSession | null;
    status: string;
    history: string[];
    offbook?: Record<string, unknown> | null;
    now?: number;
}): PracticeSnapshot | null {
    if (!session || !session.line || !RESUMABLE.includes(status)) return null;
    const attempts = session.attempts || [];
    return {
        v: SNAPSHOT_VERSION,
        savedAt: now,
        status,
        line: session.line,
        history: [...history],
        session: {
            id: session.id,
            startedAt: session.startedAt,
            startingFen: session.startingFen,
            hooksShown: session.hooksShown || 0,
            takebacks: session.takebacks || 0,
            hints: session.hints || 0,
            isRetry: !!session.isRetry,
            maxOwnMoves: session.maxOwnMoves || 0,
            bestCombo: session.bestCombo || 0,
        },
        attempts: attempts.map(plain),
        moves: Array.from(session.moves || [], (move) => {
            if (!move) return null;
            const index = attempts.indexOf(move);
            return index >= 0 ? { attempt: index } : plain(move);
        }),
        offbook: offbook || null,
    };
}

/** A snapshot this tab can still act on: the shape it was written with, and recent enough. */
export function isUsable(snapshot: unknown, now: number = Date.now(), maxAge: number = SNAPSHOT_MAX_AGE_MS): boolean {
    const snap = snapshot as PracticeSnapshot | null;
    if (!snap || snap.v !== SNAPSHOT_VERSION) return false;
    if (!snap.line || !Array.isArray(snap.history) || !Array.isArray(snap.attempts) || !Array.isArray(snap.moves)) return false;
    if (!RESUMABLE.includes(snap.status)) return false;
    return now - (snap.savedAt || 0) < maxAge;
}

/** What the offer to come back says: which line it was, and how far it got. */
export interface ResumablePractice {
    id: string;
    name: string;
    slug: string | null;
    source: string;
    /** The account's line it was, when it was one of theirs: those have no slug to be found by. */
    userLineId: string | null;
    color: string;
    ownMoves: number;
    savedAt: number;
}

/**
 * The practice waiting to be resumed, for whoever offers it. Null when there is nothing to offer.
 *
 * Nobody is taken back to a practice without asking: the page they land on is the one they asked
 * for, and this is the button on it. Only a practice they chose gets here at all — the line the home
 * page deals a visitor on its own is not something they started, so it is never kept.
 */
export function resumable(snapshot: unknown, now: number = Date.now()): ResumablePractice | null {
    if (!isUsable(snapshot, now)) return null;
    const snap = snapshot as PracticeSnapshot;
    const line = snap.line as { name?: string; slug?: string; source?: string; color?: string; id?: number | string };
    const ownMoves = snap.moves.filter((move) => move && typeof (move as { attempt?: number }).attempt === "number").length;
    // A line that was put on a board and never played is not something to come back to
    if (!ownMoves) return null;
    return {
        id: snap.session.id,
        name: line.name || "your line",
        slug: line.slug || null,
        source: line.source || "catalog",
        userLineId: line.source === "user" && line.id !== undefined && line.id !== null ? String(line.id) : null,
        color: line.color || "white",
        ownMoves,
        savedAt: snap.savedAt,
    };
}

/** The session the practice runs on, with `moves` and `attempts` sharing their records again. */
export function restoreSession(snapshot: PracticeSnapshot): { session: Omit<LiveSession, "line">; status: string; offbook: Record<string, unknown> | null } {
    const attempts = snapshot.attempts.map((move) => ({ ...move }));
    const moves = snapshot.moves.map((move) => {
        if (!move) return undefined;
        const ref = move as { attempt?: number };
        return typeof ref.attempt === "number" ? attempts[ref.attempt] : { ...(move as Record<string, unknown>) };
    });
    return {
        session: { ...snapshot.session, moves, attempts },
        status: snapshot.status,
        offbook: snapshot.offbook || null,
    };
}

/**
 * Where the way back to it goes. A catalog line by its slug, a line of the account's by its id: without
 * either, /tools/study is the picker, which is where the button had sent a line of theirs — back to the
 * page it was on.
 */
export function resumeHref(waiting: ResumablePractice): string {
    if (waiting.userLineId) return `/tools/study?userLine=${encodeURIComponent(waiting.userLineId)}`;
    return waiting.slug ? `/tools/study?line=${encodeURIComponent(waiting.slug)}` : "/tools/study";
}
